/* eslint-disable max-lines */
import React, {
    useContext, useEffect, useState
} from 'react';
import {
    KBContext
} from 'builders/builderKnowledge/knowledgeBuilderContext/KBGlobalContext';
import {
    Context
} from 'shared/context/GlobalState';
import {
    CustomTextSkeleton
} from 'shared/components/ui/text-skeleton';
import {
    PROCESS_LEVEL, STATUS_FILTER, translateChunkingStrategy, translateEmbeddingModel
} from '../config/valueMapping';
import ActionButton from './ActionButton';
import ProcessHistoryCard from './ProcessHistoryCard';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import KBDropdown, {
    KBL1FilterDropdown, KBL2FilterDropdown
} from './KBDropdown';
import {
    CONTEXTUAL_RETRIEVAL_OPTIONS, L2_PROCESS_FILTER_OPTIONS, PROCESS_FILTER_OPTIONS, PROCESS_TYPE_OPTIONS
} from '../config/dropdownOptions';
import {
    Button
} from 'shared/components/ui/button';
import QueryModal from './QueryModal';
import {
    SidebarContext
} from 'builders/builderKnowledge/knowledgeBuilderContext/KBSidebarContext';
import {
    Tooltip, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';
import KBTooltipContent from './KBTooltipContent';
import {
    Dialog, DialogContent, DialogTrigger
} from 'shared/components/ui/dialog';
import CollectionEditModal from './CollectionEditModal';
import {
    useParams
} from 'react-router-dom';
import {
    kbService
} from 'shared/api/services/PROJECT-O/KnowledgeBuilder.service';
import WebCrawlerComponent from './WebCrawlerComponent';
import GeneralInstructionSelectionModal from './GeneralInstructionSelectionModal';
import {
    cn
} from 'shared/lib/utils';
import {
    ChevronsUp, Database, FileDown, Globe, Loader2, LoaderCircle, Pencil, Play, Ratio, RefreshCcw, Search, ChevronLeft, ChevronRight, FileImage
} from 'lucide-react';

function SidebarSectionHeaderButton({
    icon, action, tooltipText, dialogContent, visibility, setVisibility
}) {
    return (
        <>
            <Dialog onOpenChange={(e) => {

                setVisibility(e);
            }} open={visibility}>
                <TooltipProvider>
                    <Tooltip>
                        <DialogTrigger asChild>
                            <TooltipTrigger asChild>
                                <Button className="p-2 py-1" onClick={action} variant="ghost">
                                    {icon}
                                </Button>
                            </TooltipTrigger>
                        </DialogTrigger>
                        {tooltipText && <KBTooltipContent tooltipText={tooltipText} />}
                    </Tooltip>
                </TooltipProvider>
                {dialogContent && <DialogContent className="w-[400px]">
                    {dialogContent}
                </DialogContent>}
            </Dialog>
        </>
    );
}

function SidebarSection({
    title, children, childrenClassName, buttons
}) {
    return (<>
        <div className="mt-10 m-8">
            <div className="flex items-center justify-between">
                <h4 className="text-muted-foreground sticky text-md">{title}</h4>
                <div>
                    {buttons?.length > 0 && buttons.map((button) => (
                        <SidebarSectionHeaderButton
                            key={button.tooltipText}
                            icon={button.icon}
                            action={button.action}
                            visibility={button.visibility}
                            setVisibility={button.setVisibility}
                            tooltipText={button.tooltipText}
                            dialogContent={button.dialogContent}
                        />
                    ))}
                </div>
            </div>
            <div className={cn('mt-3', childrenClassName)}>
                {children}
            </div>
        </div>
    </>);
}

function DataWithIcon({
    icon, children, className
}) {
    return (
        <>
            <div className={cn('flex items-center', className)}>
                {icon}
                {children}
            </div>
        </>
    );
}

export default function KBSidebar() {
    const ICON_SIZE = 15;
    const [startButtonDisabled, setStartButtonDisabled] = useState(false);
    const [buttonStartText, setButtonStartText] = useState('Start');
    const [exportButtonText, setExportButtonText] = useState('Export');
    const [isCollapsed, setIsCollapsed] = useState(false);
    const {
        collectionDetails,
        collectionLoading,
        l1Status,
        loadProcessHistory,
        processHistory,
        getMemberNameByUserId,
        processHistoryLoading,
    } = useContext(KBContext);
    const {
        addNewNotifcation
    } = useContext(Context);
    const sidebarContext = useContext(SidebarContext);
    if (!sidebarContext) {
        return <div>Context not available</div>;
    }
    const {
        setProcessType,
        processType,
        setLevelType,
        levelType,
        l1Filter,
        setL1Filter,
        l2Filter,
        setL2Filter,
        exportFilter,
        setExportFilter,
        contextualRetrievalMode,
        setContextualRetrievalMode,
        executeL1Processing,
        executeL2Processing,
        isExporting,
        executeExport,
        setEditDialogVisible,
        editDialogVisible,
        l2Instruction,
        setL2Instruction,
    } = sidebarContext;

    const {
        collectionId,
        oragID
    } = useParams();

    const handleDisplayProcessDropdowns = () => {
        switch (processType?.value) {
            case 'l1':
                return (
                    <>
                        <KBL1FilterDropdown
                            required
                            title="Filter"
                            selectedValue={l1Filter}
                            setter={setL1Filter}
                            options={() => PROCESS_FILTER_OPTIONS}
                        />
                        <div className="flex items-center gap-2">
                            <KBDropdown
                                title="Contextual Retrieval"
                                selectedValue={contextualRetrievalMode}
                                setter={setContextualRetrievalMode}
                                options={() => CONTEXTUAL_RETRIEVAL_OPTIONS}
                                tooltipText={
                                    <div className="flex flex-col gap-2">
                                        <p><span className="font-semibold">None:</span> Disables contextual retrieval, resulting in the fastest processing but the lowest retrieval accuracy.</p>
                                        <p><span className="font-semibold">Global Augmentation :</span> Balances speed and accuracy by augmenting chunks with high-level, document-specific context.</p>
                                        <p><span className="font-semibold">Global Local Augmentation :</span> Maximizes retrieval accuracy by augmenting chunks with both high-level and local context, requiring slower processing.</p>
                                    </div>
                                }
                            />
                        </div>
                    </>
                );
            case 'l2':
                return (
                    <>
                        <KBL2FilterDropdown
                            required
                            title="Filter"
                            selectedValue={l2Filter}
                            setter={setL2Filter}
                            options={() => L2_PROCESS_FILTER_OPTIONS}
                        />

                        <GeneralInstructionSelectionModal
                            instruction={l2Instruction}
                            setInstruction={setL2Instruction}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    const handleStartButtonDisable = () => {
        if (!processType) return true;
        if (startButtonDisabled || l1Status === 'checking') return true;

        if (processType.value === 'l1') {
            return l1Status === 'processing' || !l1Filter;
        }

        if (processType.value === 'l2') {
            return !l2Filter || !l2Instruction || l1Status !== 'completed';
        }

        return true;
    };

    const handleCancelProcess = (processLevel) => {
        console.info('Cancelling...');
        switch (processLevel) {
            case 'l1':
                kbService.cancelL1Processing({
                    collection_id: collectionId,
                    organization_id: oragID,
                }).then(() => {

                }).catch(() => { });
                break;
            case 'l2':
                console.warn('Still to be implemented.');
                break;
            default:
                console.warn('Unknown process level:', processLevel);
        }
    };

    useEffect(() => {
        if (l1Status === 'processing') {
            setStartButtonDisabled(false);
            setButtonStartText('Processing...');
        }

        if (['completed', 'error', 'checking', 'idle', 'cancelled'].includes(l1Status)) {
            loadProcessHistory(oragID, collectionId);
            setButtonStartText('Start');
        }
    }, [l1Status]);

    if (isCollapsed) {
        return (
            <div className="w-12 flex flex-col flex-shrink-0 font-bold border-border border-l text-foreground relative">
                <Button
                    className="absolute top-6 left-2 p-2 h-8 w-8 z-10"
                    variant="ghost"
                    onClick={() => setIsCollapsed(false)}
                >
                    <ChevronRight size={16} />
                </Button>
                <div className="flex-grow h-0 pt-12">
                    <ScrollArea className="h-full">
                        <div className="flex flex-col items-center space-y-4 py-4">
                            <SidebarSectionHeaderButton
                                icon={<Pencil size={ICON_SIZE} className="text-foreground" />}
                                action={() => { }}
                                tooltipText="Edit Collection."
                                visibility={editDialogVisible}
                                setVisibility={setEditDialogVisible}
                                dialogContent={<CollectionEditModal />}
                            />
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button className="p-2 py-1" variant="ghost" onClick={() => setIsCollapsed(false)}>
                                            <RefreshCcw size={ICON_SIZE} className="text-foreground" />
                                        </Button>
                                    </TooltipTrigger>
                                    <KBTooltipContent tooltipText="Expand and Refresh" />
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    </ScrollArea>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="w-[320px] flex flex-col flex-shrink-0 font-bold border-border border-l text-foreground relative">
                <Button
                    className="absolute top-4 left-1 p-2 h-8 w-8 z-10"
                    variant="ghost"
                    onClick={() => setIsCollapsed(true)}
                >
                    <ChevronLeft size={16} />
                </Button>

                <div className="flex-grow h-0">
                    <ScrollArea className="h-full relative">
                        <SidebarSection title="Collection Details"
                            buttons={[
                                {
                                    icon: <Pencil size={ICON_SIZE} className="text-foreground" />,
                                    action: () => { },
                                    tooltipText: 'Edit Collection.',
                                    visibility: editDialogVisible,
                                    setVisibility: setEditDialogVisible,
                                    dialogContent: <CollectionEditModal />,
                                },

                            ]}>
                            <div className="flex items-center justify-between">
                                <DataWithIcon className="text-xs gap-2"
                                    icon={<Database className="" size={ICON_SIZE} />}>
                                    <CustomTextSkeleton width={60} className="h-[1rem] font-semibold" loading={collectionLoading}>
                                        {collectionDetails?.vectorStoreType || '--'}
                                    </CustomTextSkeleton>
                                </DataWithIcon>
                                <DataWithIcon className="text-xs gap-2"
                                    icon={<Ratio className="" size={ICON_SIZE} />}>
                                    <CustomTextSkeleton width={60} className="h-[1rem] font-semibold" loading={collectionLoading}>
                                        {(collectionDetails?.dimensions) || '--'}
                                    </CustomTextSkeleton>
                                </DataWithIcon>
                            </div>
                            <div className="flex items-center justify-between mt-4">
                                <DataWithIcon className="text-xs gap-2 font-normal">
                                    <div>
                                        <p>Embedding Model</p>
                                        <CustomTextSkeleton width={60} className="h-[1rem] font-normal" loading={collectionLoading}>
                                            <p className="font-semibold">{(translateEmbeddingModel(collectionDetails?.embeddingModel))}</p>
                                        </CustomTextSkeleton>
                                    </div>
                                </DataWithIcon>
                                <DataWithIcon className="text-xs gap-2 font-normal">
                                    <div>
                                        <p>Chunking Strategy</p>
                                        <CustomTextSkeleton width={60} className="h-[1rem] font-normal" loading={collectionLoading}>
                                            <p className="font-semibold">{(translateChunkingStrategy(collectionDetails?.chunkingStrategy))}</p>
                                        </CustomTextSkeleton>
                                    </div>
                                </DataWithIcon>
                            </div>
                            <div className="flex items-center mt-4">
                                <DataWithIcon className="text-xs gap-2 font-normal">
                                    <div className="flex items-center gap-1.5">
                                        <FileImage size={ICON_SIZE} />
                                        <div>
                                            <p>PDF Page Images</p>
                                            <CustomTextSkeleton width={60} className="h-[1rem] font-normal" loading={collectionLoading}>
                                                <p className="font-semibold">{collectionDetails?.pdfPageImages ? 'Enabled' : 'Disabled'}</p>
                                            </CustomTextSkeleton>
                                        </div>
                                    </div>
                                </DataWithIcon>
                            </div>
                        </SidebarSection>
                        <SidebarSection title="Actions">
                            <ActionButton icon={l1Status === 'processing' ? <LoaderCircle className="animate-spin" size={ICON_SIZE} /> : <Play size={ICON_SIZE} />} action="toggle" label={'Process'}>
                                <p className="text-xs mt-4 mb-4 text-foreground font-normal">Process the files into embeddings to allow your data to be queried.</p>
                                <KBDropdown required title="Process Type" selectedValue={processType} setter={setProcessType} options={() => PROCESS_TYPE_OPTIONS} />

                                {handleDisplayProcessDropdowns()}
                                <div className="flex items-center justify-end mt-5 mb-3">
                                    <Button
                                        disabled={handleStartButtonDisable()}
                                        variant="ghost"
                                        className="py-2 gap-1 h-fit flex text-foreground"
                                        onClick={() => {
                                            if (l1Status === 'processing') {
                                                handleCancelProcess('l1');
                                            } else if (processType.value === 'l1') {
                                                if (!contextualRetrievalMode) {
                                                    addNewNotifcation('Please select a Contextual Retrieval Type first.', 'warning');
                                                    return;
                                                }
                                                executeL1Processing();
                                            } else if (processType.value === 'l2') {
                                                executeL2Processing();
                                            }
                                        }}
                                    >
                                        {l1Status === 'processing' ? <ChevronsUp size={16} /> : <Play size={16} />}
                                        {buttonStartText}
                                    </Button>
                                </div>
                            </ActionButton>
                            <ActionButton dialogTitle="Web Crawler" icon={<Globe size={ICON_SIZE} />} action="openDialog" label={'Web Crawler'}>
                                <WebCrawlerComponent />
                            </ActionButton>
                            <ActionButton dialogTitle="Query" icon={<Search size={ICON_SIZE} />} action="openDialog" label={'Query'}>
                                <QueryModal />
                            </ActionButton>
                            <ActionButton icon={<FileDown size={ICON_SIZE} />} action="toggle" label={'Export'}>
                                <p className="text-xs mt-4 mb-4 text-foreground font-normal">Export the files from the collection.</p>
                                <KBL2FilterDropdown
                                    required
                                    title="Filter"
                                    selectedValue={exportFilter}
                                    setter={setExportFilter}
                                    options={() => L2_PROCESS_FILTER_OPTIONS}
                                />
                                <KBDropdown
                                    required
                                    title="Level Type"
                                    selectedValue={levelType}
                                    setter={setLevelType}
                                    options={() => PROCESS_TYPE_OPTIONS}
                                />
                                <div className="flex items-center justify-end mt-5 mb-3">
                                    <Button
                                        disabled={isExporting}
                                        variant="ghost"
                                        className="py-2 gap-1 h-fit flex text-foreground"
                                        onClick={() => {
                                            setExportButtonText('Exporting...');
                                            executeExport().then(() => {
                                                setExportButtonText('Export');
                                            });
                                        }}
                                    >
                                        <FileDown size={16} />
                                        {exportButtonText}
                                    </Button>
                                </div>
                            </ActionButton>

                        </SidebarSection>
                        <SidebarSection title="Process History" buttons={[
                            {
                                icon: <RefreshCcw size={ICON_SIZE} className="text-foreground" />,
                                action: () => loadProcessHistory(oragID, collectionId),
                                tooltipText: 'Refresh the list',
                                setVisibility: () => { },
                            },

                        ]}>
                            <div className="p-2 pr-0 rounded-xl border relative">
                                <div className="h-[50vh]">
                                    <ScrollArea className="h-full">
                                        {
                                            !processHistoryLoading && processHistory?.map((processCard, index) => (
                                                <ProcessHistoryCard key={index}
                                                    processType={PROCESS_LEVEL[processCard.level_of_processing]}
                                                    processedBy={getMemberNameByUserId(processCard.user_id)}
                                                    processedWhen={new Date(processCard.timestamp)}
                                                    processFilter={STATUS_FILTER[processCard.status_filter]}
                                                    successPercentage={(processCard.success_percentage).toFixed(0)}
                                                    fileNo={processCard.total_files}
                                                />
                                            ))
                                        }{
                                            (!processHistoryLoading && processHistory?.length === 0) && <div className="text-center text-foreground font-normal text-xs">No process history available.</div>
                                        }
                                        {
                                            processHistoryLoading && <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                                                <Loader2 className="animate-spin w-6 h-6" />
                                            </div>
                                        }
                                    </ScrollArea>
                                </div>
                            </div>
                        </SidebarSection>
                    </ScrollArea>

                </div>
            </div >
        </>
    );
}